import { Component, OnInit, OnChanges, OnDestroy, Input, Output, EventEmitter, SimpleChanges, Renderer2 } from '@angular/core';
import { MatDatepickerInputEvent } from '@angular/material/datepicker';

// external libs
import moment from 'moment';

declare var ga: any;

@Component({
  selector: 'app-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss']
})
export class DatepickerComponent implements OnInit, OnChanges, OnDestroy {

  @Input()
  selectedDate;

  @Input()
  minDate;

  @Input()
  maxDate;

  @Input()
  disableDates;

  @Input()
  forTimeslot;

  @Input()
  forAmendFlight;

  @Input()
  forFlyback: boolean;

  @Input()
  labelText;

  @Output()
  dateChange: EventEmitter<any> = new EventEmitter();

  public dateInput: Date | null = null;
  public defaultMonth = '';
  public dateError: String = 'Please enter a valid date';
  public minDateObj: Date;
  public maxDateObj: Date;
  // Material datepicker predicate to disable dates
  public dateFilter: (date: Date | null) => boolean;
  public showDatepicker = false;

  private unlistenCalendarClick: (() => void) | null = null;

  constructor(private renderer: Renderer2) { }

  ngOnInit() {

    this.setCalendarOptions();
  }

  ngOnDestroy() {
    this.onPickerClosed();
  }

  onPickerOpened() {
    // MatDatepicker doesn't emit dateInput/dateChange when the user re-clicks the
    // already-selected date (see MatDatepickerContent._handleUserSelection), so we
    // listen for that click directly and force the change through.
    this.unlistenCalendarClick = this.renderer.listen('document', 'click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      const cell = target.closest?.('.mat-calendar-body-cell') as HTMLElement | null;

      if (cell && !cell.classList.contains('mat-calendar-body-disabled') &&
        cell.querySelector('.mat-calendar-body-selected') && this.dateInput) {
        this.dateChanged(this.dateInput);
      }
    });
  }

  onPickerClosed() {
    if (this.unlistenCalendarClick) {
      this.unlistenCalendarClick();
      this.unlistenCalendarClick = null;
    }
  }

  setCalendarOptions() {

    const minDateMoment = moment(this.minDate).subtract(1, 'days');
    const maxDateMoment = moment(this.maxDate).add(1, 'days');
    const selectedDateMoment = moment(this.selectedDate);

    // create moments that represent the exclusive disabled ranges for clarity
    const disableUntilMoment = minDateMoment.clone();
    const disableSinceMoment = maxDateMoment.clone();

    // Set min/max dates for Material datepicker
    this.minDateObj = minDateMoment.toDate();
    this.maxDateObj = maxDateMoment.toDate();

    // default month for timeslot page
    const month = moment(this.minDate).month() + 1;
    this.defaultMonth = month < 10 ? '0' + month : '' + month;
    this.defaultMonth += '/' + minDateMoment.year();

    // Set dateInput to Date object for Material
    this.dateInput = selectedDateMoment.isValid() ? moment(selectedDateMoment).startOf('day').toDate() : null;

    // Map old disable options to a Material date filter predicate
    // - disable dates before or equal to `disableUntilMoment`
    // - disable dates after or equal to `disableSinceMoment`
    // - disable any dates explicitly listed in `this.disableDates`
    const disabledDatesSet = new Set<string>();
    if (Array.isArray(this.disableDates)) {
      // normalize incoming disableDates to ISO date strings (YYYY-MM-DD) where possible
      this.disableDates.forEach((d: any) => {
        const m = moment(d);
        if (m.isValid()) {
          disabledDatesSet.add(m.format('YYYY-MM-DD'));
        } else if (typeof d === 'string') {
          // fallback: keep raw string
          disabledDatesSet.add(d);
        }
      });
    }

    this.dateFilter = (date: Date | null): boolean => {
      if (!date) {
        return true;
      }
      const m = moment(date);

      // disable before or equal to disableUntilMoment
      if (disableUntilMoment.isValid() && m.isSameOrBefore(disableUntilMoment, 'day')) {
        return false;
      }

      // disable after or equal to disableSinceMoment
      if (disableSinceMoment.isValid() && m.isSameOrAfter(disableSinceMoment, 'day')) {
        return false;
      }

      // explicit disabled dates
      if (disabledDatesSet.size > 0 && disabledDatesSet.has(m.format('YYYY-MM-DD'))) {
        return false;
      }

      return true;
    };
  }

  dateChanged(event: MatDatepickerInputEvent<Date> | Date | null) {
    let date: Date | null = event instanceof Date ? event : event?.value ?? null;

    if (date) {
      // normalize to midnight
      date = moment(date).startOf('day').toDate();
    }

    // Force Angular to treat it as new selection
    this.dateInput = null;

    setTimeout(() => {
      this.dateInput = date;

      if (date) {
        const formatted = moment(date).format('DD/MM/YYYY');
        this.dateChange.emit(moment(date).format('YYYY-MM-DD'));
        ga('send', 'event', 'Vanilla', 'Time Date', formatted);
      } else {
        this.dateChange.emit(null);
      }

      this.showDatepicker = false;
    });
  }



  toggleDatepicker() {
    this.showDatepicker = !this.showDatepicker;
  }

  closeDatepicker() {
    this.showDatepicker = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    // 🟢 If parent clears selectedDate → clear Material model
    if (changes.selectedDate && !changes.selectedDate.firstChange) {
      const newVal = changes.selectedDate.currentValue;

      if (!newVal) {
        this.dateInput = null;  // ← reset picker
        return;
      }

      // If parent sets a NEW date
      const m = moment(newVal);
      if (m.isValid()) {
        this.dateInput = m.toDate();
      }
    }

    if (
      (changes.minDate && !changes.minDate.firstChange) ||
      (changes.maxDate && !changes.maxDate.firstChange)
    ) {
      const currentDate = this.dateInput; // preserve
      this.setCalendarOptions();
      this.dateInput = currentDate; // restore
    }
  }

}
